import { useSEO } from "@/hooks/use-seo";
import { useLanguage } from "@/contexts/LanguageContext";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";

export default function Privacy() {
  const { language } = useLanguage();
  const nl = language === "nl";

  useSEO({
    title: nl ? "Privacybeleid | Wrkt Digital" : "Privacy Policy | Wrkt Digital",
    description: nl
      ? "Privacybeleid van Wrkt Digital BV. Hoe wij omgaan met je persoonsgegevens."
      : "Privacy Policy of Wrkt Digital BV. How we handle your personal data.",
    path: "/privacy",
    lang: nl ? "nl" : "en",
  });

  return (
    <div className="min-h-screen bg-[#0B1A0F] text-white">
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl sm:text-4xl font-display font-bold mb-12" data-testid="text-privacy-title">
              {nl ? "Privacybeleid" : "Privacy Policy"}
            </h1>

            <div className="prose prose-invert prose-green max-w-none space-y-8 text-white/70 leading-relaxed">
              <Section title={nl ? "Wie is verantwoordelijk voor de verwerking?" : "Who is responsible for data processing?"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV, gevestigd aan de Herengracht 372, 1016 CH Amsterdam, is verantwoordelijk voor de verwerking van persoonsgegevens zoals weergegeven in dit privacybeleid."
                    : "Wrkt Digital BV, located at Herengracht 372, 1016 CH Amsterdam, is responsible for processing personal data as described in this privacy policy."}
                </p>
                <ul>
                  <li>{nl ? "Website" : "Website"}: <a href="https://wrkt.nl" className="text-[#4CEAAC] hover:underline">https://wrkt.nl</a></li>
                  <li>{nl ? "Adres" : "Address"}: Herengracht 372, 1016 CH Amsterdam</li>
                  <li>{nl ? "Telefoon" : "Phone"}: +31 (0)20 896 51 36</li>
                  <li>{nl ? "E-mail" : "Email"}: <a href="mailto:contact@wrkt.nl" className="text-[#4CEAAC] hover:underline">contact@wrkt.nl</a></li>
                </ul>
              </Section>

              <Section title={nl ? "Persoonsgegevens die wij verwerken" : "Personal data we process"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV verwerkt persoonsgegevens doordat je gebruik maakt van onze diensten en/of omdat je deze gegevens zelf aan ons verstrekt via contactformulieren op onze website. Daarnaast maken wij gebruik van Meta Pixel, Google Analytics, en Microsoft Clarity voor het analyseren van websitegebruik."
                    : "Wrkt Digital BV processes personal data because you use our services and/or because you provide this data to us through contact forms on our website. Additionally, we use Meta Pixel, Google Analytics, and Microsoft Clarity to analyze website usage."}
                </p>
              </Section>

              <Section title={nl ? "Bijzondere en/of gevoelige persoonsgegevens" : "Special and/or sensitive personal data"}>
                <p>
                  {nl
                    ? "Onze website heeft niet de intentie gegevens te verzamelen van bezoekers die jonger zijn dan 16 jaar, tenzij ze toestemming hebben van ouders of voogd. We kunnen echter niet controleren of een bezoeker ouder dan 16 is. Wij raden ouders dan ook aan betrokken te zijn bij de online activiteiten van hun kinderen. Als je ervan overtuigd bent dat wij zonder toestemming persoonlijke gegevens hebben verzameld over een minderjarige, neem dan contact met ons op via contact@wrkt.nl, dan verwijderen wij deze informatie."
                    : "Our website does not intend to collect data from visitors under 16 years of age, unless they have parental or guardian consent. However, we cannot verify if a visitor is over 16. We therefore encourage parents to be involved in their children's online activities. If you believe we have collected personal data from a minor without consent, please contact us at contact@wrkt.nl and we will delete this information."}
                </p>
              </Section>

              <Section title={nl ? "Doel en grondslag van de verwerking" : "Purpose and legal basis for processing"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV verwerkt jouw persoonsgegevens voor de volgende doelen:"
                    : "Wrkt Digital BV processes your personal data for the following purposes:"}
                </p>
                <ul>
                  <li>{nl ? "Om je te kunnen bellen of e-mailen indien dit nodig is om onze dienstverlening uit te voeren." : "To contact you by phone or email if necessary to carry out our services."}</li>
                  <li>{nl ? "Om onze producten en diensten bij je af te leveren." : "To deliver our products and services to you."}</li>
                  <li>{nl ? "Om ons websitegebruik te analyseren en de gebruikerservaring te verbeteren." : "To analyze website usage and improve user experience."}</li>
                </ul>
              </Section>

              <Section title={nl ? "Bewaartermijn" : "Data retention"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV bewaart je persoonsgegevens niet langer dan strikt nodig is om de doelen te realiseren waarvoor je gegevens worden verzameld."
                    : "Wrkt Digital BV does not retain your personal data for longer than is strictly necessary to achieve the purposes for which your data is collected."}
                </p>
              </Section>

              <Section title={nl ? "Delen van persoonsgegevens met derden" : "Sharing personal data with third parties"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV verstrekt uitsluitend aan derden en alleen als dit nodig is voor de uitvoering van onze overeenkomst met jou of om te voldoen aan een wettelijke verplichting."
                    : "Wrkt Digital BV only provides data to third parties if this is necessary for the execution of our agreement with you or to comply with a legal obligation."}
                </p>
              </Section>

              <Section title={nl ? "Cookies" : "Cookies"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV gebruikt technische, functionele en analytische cookies die geen inbreuk maken op je privacy. Een cookie is een klein tekstbestand dat bij het eerste bezoek aan deze website wordt opgeslagen op jouw computer, tablet of smartphone. De cookies die wij gebruiken zijn noodzakelijk voor de technische werking van de website en jouw gebruiksgemak. Daarnaast gebruiken wij cookies van Meta, Google Analytics, HubSpot en Microsoft Clarity. Je kunt je afmelden voor cookies door je internetbrowser zo in te stellen dat deze geen cookies meer opslaat. Daarnaast kun je ook alle informatie die eerder is opgeslagen via de instellingen van je browser verwijderen."
                    : "Wrkt Digital BV uses technical, functional, and analytical cookies that do not infringe on your privacy. A cookie is a small text file that is stored on your computer, tablet, or smartphone when you first visit this website. The cookies we use are necessary for the technical operation of the website and your ease of use. Additionally, we use cookies from Meta, Google Analytics, HubSpot, and Microsoft Clarity. You can opt out of cookies by configuring your internet browser to no longer store cookies. You can also delete all previously stored information through your browser settings."}
                </p>
              </Section>

              <Section title={nl ? "Gegevens inzien, aanpassen of verwijderen" : "Viewing, modifying, or deleting data"}>
                <p>
                  {nl
                    ? "Je hebt het recht om je persoonsgegevens in te zien, te corrigeren of te verwijderen. Daarnaast heb je het recht om je eventuele toestemming voor de gegevensverwerking in te trekken of bezwaar te maken tegen de verwerking van jouw persoonsgegevens door Wrkt Digital BV en heb je het recht op gegevensoverdraagbaarheid."
                    : "You have the right to view, correct, or delete your personal data. You also have the right to withdraw any consent for data processing, object to the processing of your personal data by Wrkt Digital BV, and the right to data portability."}
                </p>
                <p>
                  {nl
                    ? "Je kunt een verzoek tot inzage, correctie, verwijdering of gegevensoverdraging van je persoonsgegevens sturen naar contact@wrkt.nl. We reageren zo snel mogelijk, maar uiterlijk binnen vier weken, op jouw verzoek."
                    : "You can send a request for access, correction, deletion, or transfer of your personal data to contact@wrkt.nl. We will respond to your request as soon as possible, but no later than within four weeks."}
                </p>
                <p>
                  {nl
                    ? "Je kunt ook een klacht indienen bij de nationale toezichthouder, de Autoriteit Persoonsgegevens. Dat kan via de volgende link: "
                    : "You can also file a complaint with the national supervisory authority, the Dutch Data Protection Authority (Autoriteit Persoonsgegevens). You can do so via the following link: "}
                  <a
                    href="https://autoriteitpersoonsgegevens.nl/nl/contact-met-de-autoriteit-persoonsgegevens/tip-ons"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4CEAAC] hover:underline"
                  >
                    autoriteitpersoonsgegevens.nl
                  </a>
                </p>
              </Section>

              <Section title={nl ? "Beveiliging" : "Security"}>
                <p>
                  {nl
                    ? "Wrkt Digital BV neemt de bescherming van jouw gegevens serieus en neemt passende maatregelen om misbruik, verlies, onbevoegde toegang, ongewenste openbaarmaking en ongeoorloofde wijziging tegen te gaan. Als jij het idee hebt dat jouw gegevens toch niet goed beveiligd zijn of er aanwijzingen zijn van misbruik, neem dan contact op via contact@wrkt.nl."
                    : "Wrkt Digital BV takes the protection of your data seriously and takes appropriate measures to prevent misuse, loss, unauthorized access, unwanted disclosure, and unauthorized modification. If you feel that your data is not properly secured or if there are indications of misuse, please contact us at contact@wrkt.nl."}
                </p>
              </Section>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-display font-semibold text-white/90 mb-3">{title}</h2>
      {children}
    </section>
  );
}
